import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";
import { admin_login_log_details } from "../admin.api";
import Loader from "../../../components/loader/loader";
import Snackbar from "../../../components/snackbar/snackbar";
import "./style.css";

const getStatusInfo = (status) => {
  const statusMap = {
    success: {
      label: "Success",
      class: "successs",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
      )
    },
    failed: {
      label: "Failed",
      class: "danger",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="15" y1="9" x2="9" y2="15"></line>
          <line x1="9" y1="9" x2="15" y2="15"></line>
        </svg>
      )
    },
  };
  const defaultIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"></circle>
      <line x1="12" y1="16" x2="12" y2="12"></line>
      <line x1="12" y1="8" x2="12.01" y2="8"></line>
    </svg>
  );
  return statusMap[status] || {
    label: status?.charAt(0).toUpperCase() + status?.slice(1),
    class: "secondary",
    icon: defaultIcon
  };
};

export default function LoginLogDetails(props) {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackMessage, setSnackMessage] = useState("");
  const [isSnackError, setIsSnackError] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);

  useEffect(() => {
    fetchDetails();
  }, [id]);

  const fetchDetails = async () => {
    setLoading(true);
    try {
      const res = await admin_login_log_details(id);
      if (res?.success) {
        setData(res.data);
      } else {
        setSnackMessage("Log not found");
        setIsSnackError(true);
        setSnackOpen(true);
      }
    } catch (error) {
      console.error("Error fetching login log details:", error);
      setSnackMessage("Error loading details");
      setIsSnackError(true);
      setSnackOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const snackbarClose = () => {
    setSnackOpen(false);
  };

  if (loading) return <Loader open={true} />;

  if (!data) return (
    <div className="container-fluid mt-4">
      <div className="alert alert-warning">
        <p>Login log details not found.</p>
        <button className="btn btn-primary btn-sm" onClick={() => props.history.goBack()}>Go Back</button>
      </div>
    </div>
  );

  const DataRow = ({ label, value, isStatus }) => (
    <div className="row d-flex flex-nowrap align-items-center py-2 border-bottom mx-0">
      <div className="col-3 font-weight-bold text-muted text-nowrap">{label}</div>
      <div className="col-1 text-center">:</div>
      <div className="col-8">
        {isStatus ? (
          <span className={`status-badge ${getStatusInfo(value).class}`}>
            <span className="status-icon">{getStatusInfo(value).icon}</span>
            <span className="status-label">{getStatusInfo(value).label}</span>
          </span>
        ) : (
          <span className="text-dark fw-bold">{value || "--"}</span>
        )}
      </div>
    </div>
  );

  return (
    <div className="container-fluid mt-4">
      <Snackbar
        message={snackMessage}
        error={isSnackError}
        open={snackOpen}
        close={snackbarClose}
      />
      
      <div className="view-container shadow-sm border-0" style={{ borderRadius: '12px' }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center">
            <button
              style={{
                backgroundColor: "#0a223d",
                color: "#fff",
                border: "solid 1px #0a223d",
              }}
              type="button"
              className="btn btn-warning btn-sm mr-3"
              onClick={() => props.history.goBack()}
            >
              Back
            </button>
            <h5 className="mb-0 fw-bold">Login Event Activity Details</h5>
          </div>
          <div className="text-muted small">Log ID: <b>#{data.id}</b></div>
        </div>

        <hr />

        <div className="data px-2">
          <h6 className="section-title mb-3 mt-2 text-primary fw-bold small text-uppercase" style={{ letterSpacing: '1px' }}>User Information</h6>
          <DataRow label="Name" value={data.name} />
          <DataRow label="Email" value={data.email} />
          <DataRow label="User Type" value={data.user_type?.replace(/_/g, ' ').toUpperCase()} />
          <DataRow label="Company" value={data.company_name} />

          <h6 className="section-title mb-3 mt-4 text-primary fw-bold small text-uppercase" style={{ letterSpacing: '1px' }}>Technical Context</h6>
          <DataRow label="Status" value={data.status} isStatus={true} />
          <DataRow label="IP Address" value={data.ip_address} />
          <DataRow label="Method" value={data.login_type} />
          <DataRow label="Browser" value={data.browser} />
          <DataRow label="Platform / Device" value={`${data.platform || '--'} / ${data.device_type || '--'}`} />

          <h6 className="section-title mb-3 mt-4 text-primary fw-bold small text-uppercase" style={{ letterSpacing: '1px' }}>Session Temporal Details</h6>
          <DataRow label="Logged In" value={data.logged_in_at ? moment(data.logged_in_at).format("DD MMMM YYYY, hh:mm:ss A") : "--"} />
          <DataRow label="Logged Out" value={data.logged_out_at ? moment(data.logged_out_at).format("DD MMMM YYYY, hh:mm:ss A") : "Active Session"} />
          <DataRow label="Duration" value={data.session_duration ? `${Math.floor(data.session_duration / 60)} minutes` : "--"} />

          {data.failure_reason && (
            <div className="alert alert-danger mt-4 rounded-3 border-0">
              <h6 className="fw-bold mb-2">Failure Trace Information</h6>
              <p className="mb-0 small">{data.failure_reason}</p>
            </div>
          )}

          <div className="mt-5 pt-3 border-top">
            <label className="form-label small text-muted text-uppercase fw-bold">User Agent Signature</label>
            <div className="bg-light p-3 rounded-3 text-muted" style={{ fontSize: '11px', lineHeight: '1.6', wordBreak: 'break-all', border: '1px dashed #ddd' }}>
              {data.user_agent}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
